import { Box, Grid, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import MetricsGrid from './MetricsGrid';
import ChartCard from './ChartCard';
import CohortRetentionTable from './CohortRetentionTable';
import { AdminAnalyticsData } from '../../../types/admin';
import { formatNumber, formatPercentage, formatDuration } from '../../../utils/formatters';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TouchAppIcon from '@mui/icons-material/TouchApp';

interface UserEngagementTabProps {
  data: AdminAnalyticsData;
  loading?: boolean;
}

/**
 * UserEngagementTab - User engagement analytics
 *
 * Displays:
 * - Engagement KPIs (DAU, MAU, DAU/MAU ratio, session duration, actions per user)
 * - Feature usage bar chart
 * - Cohort retention heatmap table
 * - Activity heatmap (future enhancement)
 */
export default function UserEngagementTab({ data, loading }: UserEngagementTabProps) {
  // Prepare metrics for cards
  const metrics = [
    {
      title: 'Daily Active Users',
      value: formatNumber(data.engagement.dau),
      subtitle: 'Last 24 hours',
      icon: <PeopleIcon />,
      color: '#1976d2',
      trend: {
        value: 5.2,
        label: 'vs yesterday',
        direction: 'up' as const,
      },
    },
    {
      title: 'Monthly Active Users',
      value: formatNumber(data.engagement.mau),
      subtitle: 'Last 30 days',
      icon: <PeopleIcon />,
      color: '#9c27b0',
      trend: {
        value: 6.7,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'DAU/MAU Ratio',
      value: formatPercentage(data.engagement.dau_mau_ratio, 1),
      subtitle: 'Daily stickiness',
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
      trend: {
        value: 2.1,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Avg Session Duration',
      value: formatDuration(data.engagement.avg_session_duration),
      subtitle: 'Per user session',
      icon: <AccessTimeIcon />,
      color: '#ed6c02',
      trend: {
        value: 3.5,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Actions Per User',
      value: data.engagement.actions_per_user.toFixed(1),
      subtitle: 'Avg daily actions',
      icon: <TouchAppIcon />,
      color: '#0288d1',
      trend: {
        value: 4.2,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
  ];

  // Prepare feature usage data
  const featureNames = data.featureUsage.map((f) => f.feature);
  const featurePercentages = data.featureUsage.map((f) => f.percentage);

  return (
    <Box>
      {/* Top row: Engagement KPIs */}
      <Box sx={{ mb: 4 }}>
        <MetricsGrid metrics={metrics} loading={loading} />
      </Box>

      {/* Charts and Tables */}
      <Grid container spacing={3}>
        {/* Feature Usage Chart */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Feature Usage"
            subtitle="Percentage of users who have used each feature"
            loading={loading}
            height={450}
          >
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: featureNames,
                  tickLabelStyle: {
                    angle: -45,
                    textAnchor: 'end',
                    fontSize: 11,
                  },
                },
              ]}
              yAxis={[
                {
                  label: 'Usage %',
                  max: 100,
                },
              ]}
              series={[
                {
                  data: featurePercentages,
                  label: 'User Adoption %',
                  color: '#1976d2',
                },
              ]}
              grid={{ horizontal: true }}
              layout="vertical"
            />
          </ChartCard>
        </Grid>

        {/* Feature Usage Details */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Feature Usage Details"
            subtitle="Detailed breakdown of feature engagement"
            loading={loading}
            height={450}
          >
            <Box sx={{ overflowY: 'auto', height: '100%', p: 2 }}>
              {data.featureUsage.map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom:
                      index < data.featureUsage.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {feature.feature}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {feature.percentage}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(feature.unique_users)} users • {formatNumber(feature.usage_count)}{' '}
                    total actions
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      height: 8,
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${feature.percentage}%`,
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </ChartCard>
        </Grid>

        {/* Cohort Retention Table */}
        <Grid item xs={12}>
          <ChartCard
            title="Cohort Retention Analysis"
            subtitle="User retention rates by monthly cohort (percentage of users returning)"
            loading={loading}
            height={500}
          >
            <CohortRetentionTable data={data.cohortRetention} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
